import { useQuery } from "@tanstack/react-query";

import axios from "axios";
import { useTixologi } from "@punchup/data/legacy/hooks";
import { tixologiUrl } from "../../api-base";

/**
 * The venue's default ticket fees, as ticketing-api's GET /ticket_types/fee-defaults
 * answers them. A form prefill only: each ticket type keeps its own fixed_fee /
 * percentage_fee, and no price is computed from this. `percentage_fee` is in PERCENT
 * (5.5), the unit ticket_types use on the wire; the event wizard's form works in a
 * decimal (0.055).
 */
export type TicketFeeDefaults = {
  configured: boolean;
  fixed_fee: number;
  percentage_fee: number;
};

export const TICKET_FEE_DEFAULTS_QUERY_KEY = "ticketFeeDefaults";

/**
 * Statsig gate for the whole feature: the Ticket fees block in Default fees and
 * the event wizard prefill. Off (or unknown to Statsig) means today's wizard —
 * copied ticket types keep their own fees — so a venue is opted in deliberately.
 */
export const TICKET_FEE_DEFAULTS_GATE = "venue_default_ticket_fees";

type UseTicketFeeDefaultsReturn = {
  data: TicketFeeDefaults | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  hasTixologi: boolean;
  refetch: () => void;
};

export function useTicketFeeDefaults({
  venuePageId,
}: {
  venuePageId?: string;
}): UseTicketFeeDefaultsReturn {
  const { tixologiData } = useTixologi({
    id: venuePageId || "",
    isComedian: false,
  });
  const hasTixologi = !!venuePageId && !!tixologiData?.token;
  // The token lookup itself is still in flight: report it as loading, so the block
  // doesn't flash "not selling through Punchup" for a venue that is. Without a
  // venuePageId the lookup never runs, so that case is not loading.
  const isResolvingTixologi = !!venuePageId && tixologiData === undefined;

  const { data, isLoading, isError, error, refetch } =
    useQuery<TicketFeeDefaults>({
      queryKey: [TICKET_FEE_DEFAULTS_QUERY_KEY, venuePageId],
      queryFn: async (): Promise<TicketFeeDefaults> => {
        const response = await axios.get(
          tixologiUrl("/ticket_types/fee-defaults"),
          { headers: { Authorization: `Bearer ${tixologiData?.token}` } }
        );
        return response.data;
      },
      enabled: hasTixologi,
    });

  return {
    data,
    isLoading: isResolvingTixologi || isLoading,
    isError,
    error,
    hasTixologi,
    refetch,
  };
}
